import React, { useCallback, useEffect, useState } from 'react';
import { GitPullRequest, Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n || 0).toFixed(2)}`;

const DeltaTable = ({ lines }) => (
  <div className="border rounded-lg overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-gray-50">
        <tr>
          <th className="text-left p-2">Product</th>
          <th className="text-right p-2">Ordered</th>
          <th className="text-right p-2">Requested</th>
          <th className="text-right p-2">Impact</th>
        </tr>
      </thead>
      <tbody>
        {(lines || []).map((ln) => (
          <tr key={ln.product_id} className="border-t">
            <td className="p-2">{ln.product_name}</td>
            <td className="text-right p-2 text-gray-500">{ln.original_qty}</td>
            <td className="text-right p-2 font-medium">{ln.requested_qty}</td>
            <td className={`text-right p-2 font-medium ${ln.amount_delta < 0 ? 'text-green-600' : 'text-amber-600'}`}>
              {money(ln.amount_delta)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ChangeRequestsSection = ({ onResolved }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    try {
      setRequests(await adminService.getOrderChangeRequests('pending'));
    } catch {
      // A missing/failed fetch must not break the orders page.
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (req) => {
    setBusyId(req.id);
    try {
      const result = await adminService.approveOrderChangeRequest(req.id);
      const net = result.net_amount_delta || 0;
      toast.success(
        net < 0
          ? `Approved — credit memo ${result.credit_memo?.credit_memo_number} issued`
          : net > 0
            ? `Approved — additional invoice ${result.supplemental_invoice?.invoice_number} created`
            : 'Approved — no financial change',
      );
      await load();
      onResolved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve the change request');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    setBusyId(rejectTarget.id);
    try {
      await adminService.rejectOrderChangeRequest(rejectTarget.id, rejectReason.trim());
      toast.success('Change request declined');
      setRejectTarget(null);
      setRejectReason('');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline the change request');
    } finally {
      setBusyId(null);
    }
  };

  if (loading || requests.length === 0) return null;

  return (
    <Card className="border-indigo-200" data-testid="change-requests-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <GitPullRequest className="h-4 w-4 text-indigo-600" />
          Change Requests
          <Badge className="bg-indigo-100 text-indigo-700" data-testid="change-requests-count">
            {requests.length} pending
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {requests.map((req) => {
          const net = req.computed_delta?.net_amount_delta || 0;
          return (
            <div
              key={req.id}
              className="p-3 sm:p-4 rounded-lg border bg-white space-y-3"
              data-testid={`change-request-${req.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">
                    {req.order_number}
                    <span className="ml-2 text-sm font-normal text-gray-500">{req.client_name}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Requested by {req.requested_by_name} ·{' '}
                    {req.requested_at ? new Date(req.requested_at).toLocaleString() : ''} ·
                    invoice {req.invoice_number}
                  </p>
                </div>
                <div className="sm:text-right">
                  <p className="text-xs text-gray-500 uppercase">Net impact</p>
                  <p
                    className={`text-lg font-bold ${net < 0 ? 'text-green-600' : net > 0 ? 'text-amber-600' : 'text-gray-500'}`}
                    data-testid={`change-request-net-${req.id}`}
                  >
                    {money(net)}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {net < 0 ? 'credit to client' : net > 0 ? 'client owes more' : 'no change'}
                  </p>
                </div>
              </div>

              <DeltaTable lines={req.computed_delta?.line_changes} />

              <p className="text-sm text-gray-700">
                <span className="text-gray-500">Client reason:</span> {req.reason}
              </p>

              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  className="min-h-[44px] border-red-300 text-red-600 hover:bg-red-50"
                  onClick={() => { setRejectTarget(req); setRejectReason(''); }}
                  disabled={busyId === req.id}
                  data-testid={`reject-change-request-${req.id}`}
                >
                  <X className="h-4 w-4 mr-1" /> Decline
                </Button>
                <Button
                  className="min-h-[44px] bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => handleApprove(req)}
                  disabled={busyId === req.id}
                  data-testid={`approve-change-request-${req.id}`}
                >
                  <Check className="h-4 w-4 mr-1" />
                  {busyId === req.id ? 'Working…' : 'Approve'}
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent data-testid="reject-change-request-dialog">
          <DialogHeader>
            <DialogTitle>Decline change request</DialogTitle>
            <DialogDescription>
              The order stays exactly as it is. The client sees your reason.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase text-gray-500">Reason (required)</Label>
            <Textarea
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. This batch is already in production for tomorrow's route"
              className="mt-1"
              data-testid="reject-change-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={rejectReason.trim().length < 3 || busyId === rejectTarget?.id}
              data-testid="confirm-reject-change-request"
            >
              Decline request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
